import { IAdjudicationsDashBoardConfigResponse } from '@publishing-engine/domain/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  fetchAdjudicationList,
  fetchAdjudicationsDashBoardConfig,
  fetchAdjudicationsFilters,
  getAdjudicationListCount,
  ISubmissionSortOption
} from 'src/api/queries';
import { TABLE_PAGE_LIMIT } from 'src/constants';

export type IAdjudicationsFilterStatus = 'completed' | 'active';

export type IAdjudicationsListFilters = {
  journalId: Array<string>;
  expertRecommendations: Array<string>;
  expertPhases: Array<string>;
  searchParams: string;
  workflowPhase: Array<string>;
};

export type IAdjudicationsListFiltersResponse = {
  journals: Array<{ id: string; name: string; shortname: string }>;
  expertRecommendations: Array<string>;
  workflowPhase: Array<string>;
  expertPhases: Array<string>;
  workflowPhases: Array<string>;
};

export const adjudicationsDefaultValues: IAdjudicationsListFilters = {
  journalId: [],
  expertRecommendations: [],
  expertPhases: [],
  searchParams: '',
  workflowPhase: []
};

export const useAdjudicationsList = (
  status: IAdjudicationsFilterStatus,
  filters: IAdjudicationsListFilters,
  defaultFilters?: IAdjudicationsDashBoardConfigResponse,
  sort?: ISubmissionSortOption,
  limit: number = TABLE_PAGE_LIMIT
) => {
  const [page, setPage] = useState<number>(1);
  const offset = (page - 1) * limit;
  type FilterMap = Record<string, string[]>;
  let result: FilterMap = {};
  if (defaultFilters?.filters) {
    result = defaultFilters.filters.reduce((acc, filter) => {
      acc[filter.key] = filter.items.map(item => item.value);
      return acc;
    }, {} as FilterMap);
  }
  const filterPayload = {
    ...filters,
    workflowPhase: filters?.workflowPhase?.length
      ? filters.workflowPhase
      : result?.workflowPhase || [],
    expertPhases: filters?.expertPhases?.length
      ? filters?.expertPhases
      : result?.expertPhases || []
  };
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'adjudication-list-data',
      { page, filters, sort, status, limit }
    ],
    queryFn: () => fetchAdjudicationList(offset, limit, filterPayload, sort),
    enabled: !!defaultFilters
  });

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return {
    page,
    setPage,
    data,
    isLoading,
    isError,
    totalPages
  };
};
export const useAdjudicationsTotal = () => {
  return useQuery({
    queryKey: ['adjudications-list-total'],
    queryFn: () => getAdjudicationListCount()
  });
};

export const useAdjudicationsDashBoardConfig = (
  view: string,
  workspaceId?: string
) =>
  useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchAdjudicationsDashBoardConfig({ view, workspaceId })
  });

export const useAdjudicationsFilters = (
  filterStatus: IAdjudicationsFilterStatus
) => {
  return useQuery({
    queryKey: ['adjudications-filter', { filterStatus }],
    queryFn: () => fetchAdjudicationsFilters(filterStatus)
  });
};
